/**
 * A próxima leitura só começa depois da resposta anterior. Em HTTP/1.1, intervalos
 * fixos com respostas lentas ocupavam as seis conexões e enfileiravam as escritas.
 * Cada dono cancela o transporte ao sair ou esconder a aba; voltar faz catch-up.
 */
export function startConversationPolling(
  read: (signal: AbortSignal) => Promise<boolean>,
  floorMs: number,
): () => void {
  let stopped = false;
  let running = false;
  let resume = false;
  let delay = floorMs;
  let controller: AbortController | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const visible = () => document.visibilityState !== 'hidden';

  const run = async (): Promise<void> => {
    if (stopped || running || !visible()) return;
    running = true;
    const attempt = new AbortController();
    controller = attempt;
    let changed = false;
    try { changed = await read(attempt.signal); }
    catch { /* O consumidor mantém a causa e a última leitura na tela. */ }
    finally {
      running = false;
      if (controller === attempt) controller = undefined;
      if (!stopped && visible()) {
        delay = changed || resume ? floorMs : Math.min(30_000, delay * 2);
        const next = resume ? 0 : delay;
        resume = false;
        timer = setTimeout(() => void run(), next);
      }
    }
  };
  const visibility = (): void => {
    clearTimeout(timer);
    if (document.visibilityState === 'hidden') { controller?.abort(); return; }
    delay = floorMs;
    if (running) resume = true;
    else void run();
  };
  document.addEventListener('visibilitychange', visibility);
  void run();
  return () => {
    stopped = true;
    clearTimeout(timer);
    controller?.abort();
    document.removeEventListener('visibilitychange', visibility);
  };
}
